/**
 * Provides the sample tap scroll circle visual used for demos.
 * デモ用に使用されるサンプルのタップスクロールビジュアルを提供するモジュール。
 */

import type { TapScrollCircleProps, TapScrollCircleRenderProps } from "./TapScrollCircle.tsx"

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

export const tapScrollCircleSampleVisual: NonNullable<TapScrollCircleProps["renderVisual"]> = ({ dragState, normalizedDistance, sizeScale, size }: TapScrollCircleRenderProps) => {
    const radius = Math.max(size / 2, 1)
    const stretchY = 1 + normalizedDistance * 0.65
    const stretchX = Math.max(0.65, 1 - normalizedDistance * 0.25)
    const tail = dragState.direction * normalizedDistance * 26 * sizeScale
    const pupilScale = 0.8 + normalizedDistance * 0.18
    const tailBase = 3 * sizeScale
    const baseHeight = 6 * sizeScale
    const coreSize = 22 * sizeScale
    const rodHeight = Math.abs(tail) + baseHeight
    const rodTranslate = tail > 0 ? tailBase : -Math.abs(tail) - tailBase
    const rodWidth = Math.max(2.5, 3 * sizeScale)
    const limitedX = clamp(dragState.offsetX, -radius, radius)
    const limitedY = clamp(dragState.offsetY, -radius, radius)
    const pupilRange = radius * 0.35
    const pupilX = (limitedX / radius) * pupilRange
    const pupilY = (limitedY / radius) * pupilRange
    const highlightX = pupilX * 0.45
    const highlightY = pupilY * 0.45
    const highlightSize = Math.max(coreSize * 0.38, 6)
    const highlightOpacity = 0.65 + normalizedDistance * 0.2
    const active = dragState.active

    return (
        <>
            <div
                className="absolute inset-0 rounded-full border border-white/40 bg-linear-to-br from-[#1d4ed8]/60 via-[#60a5fa]/55 to-[#bfdbfe]/40 shadow-md"
                style={{
                    transform: `scale(${stretchX}, ${stretchY})`,
                    transition: active ? "40ms transform ease-out" : "200ms ease transform",
                }}
            />
            <div
                className="absolute top-1/2 left-1/2 rounded-full border border-white/50 bg-white/85"
                style={{
                    width: coreSize,
                    height: coreSize,
                    transform: `translate(calc(-50% + ${pupilX}px), calc(-50% + ${pupilY}px)) scale(${stretchX}, ${pupilScale * stretchY})`,
                    transition: active ? "70ms transform ease-out" : "200ms ease transform",
                }}
            />
            <div
                className="absolute top-1/2 left-1/2 rounded-full bg-white/80"
                style={{
                    width: highlightSize,
                    height: highlightSize,
                    transform: `translate(calc(-50% + ${highlightX}px), calc(-50% + ${highlightY}px)) scale(${stretchX}, ${stretchY})`,
                    opacity: highlightOpacity,
                    boxShadow: "0 0 8px rgba(255,255,255,0.45)",
                    transition: active ? "120ms opacity 150ms, 120ms transform ease-out ease-out" : "220ms ease transform, 240ms opacity ease",
                }}
            />
            <div
                className="absolute top-1/2 left-1/2 rounded-full bg-white/50"
                style={{
                    width: rodWidth,
                    height: rodHeight,
                    transform: `translate(-50%, ${rodTranslate}px)`,
                    opacity: normalizedDistance,
                    transition: active ? "40ms height, 60ms opacity ease-out ease-out" : "200ms ease height, 120ms ease opacity",
                }}
            />
        </>
    )
}
